/**
 * Sources > URLs Page (Spec 064, 065)
 *
 * Shows URL-level citation data with URL type badges, filters, and trend chart.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { apiGet } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Select } from '../components/Select.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { createEvaluation } from '../api/pageEvaluations.js';
import { useToast } from '../hooks/useToast.js';
import { useFullBleed } from '../lib/layout-width.js';

interface UrlItem {
  url: string;
  url_type: string;
  domain_type: string | null;
  domain_name: string | null;
  mention_count: number;
  engine_count: number;
  engines: string[];
  last_cited: string | null;
  brand_mentioned: boolean | null;
  /**
   * Spec 089c — sum of Search Console impressions for this URL across all
   * AI surfaces (ai_overview + ai_mode + featured_snippet + all + other) over
   * the last 28 days.
   *
   * Present on every row when `flag.subsystem.search_console = true`.
   * Absent on every row when the flag is off — used by the SPA to decide
   * whether to render the "GSC impressions (28d)" column at all.
   */
  gsc_impressions_28d?: number;
}

interface UrlsResponse {
  data: UrlItem[];
  total: number;
  limit: number;
  offset: number;
}

interface UrlTypeDistribution {
  data: Array<{ url_type: string; count: number }>;
}

const URL_TYPE_LABELS: Record<string, string> = {
  article: 'Article',
  how_to_guide: 'How-to guide',
  product_page: 'Product page',
  category_page: 'Category page',
  comparison: 'Comparison',
  profile: 'Profile',
  listicle: 'Listicle',
  other: 'Other',
};

const URL_TYPE_COLORS: Record<string, string> = {
  article: 'bg-blue-100 text-blue-700',
  how_to_guide: 'bg-green-100 text-green-700',
  product_page: 'bg-purple-100 text-purple-700',
  category_page: 'bg-amber-100 text-amber-700',
  comparison: 'bg-pink-100 text-pink-700',
  profile: 'bg-teal-100 text-teal-700',
  listicle: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700',
};

function UrlTypeBadge({ type }: { type: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${URL_TYPE_COLORS[type] ?? URL_TYPE_COLORS.other}`}>
      {URL_TYPE_LABELS[type] ?? type}
    </span>
  );
}

export function UrlsPage() {
  useFullBleed();
  const { selectedBrand, user } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [urls, setUrls] = useState<UrlItem[]>([]);
  const [total, setTotal] = useState(0);
  const [typeDistribution, setTypeDistribution] = useState<Array<{ url_type: string; count: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlTypeFilter, setUrlTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [evaluatingUrl, setEvaluatingUrl] = useState<string | null>(null);

  // Phase 9 Session C gap 083-2: only admins see the inline "Evaluate" CTA.
  const canEvaluate = hasRole('rule_admin') || hasRole('orbit:admin');

  // NOTE: a "Latest score" column was considered but would require an N+1
  // call to /pages/evaluations/by-url per row (there is no batched endpoint
  // yet). Deferred to a P2 follow-up — see gap 083-2 comment in the report.
  const handleEvaluate = useCallback(
    async (url: string) => {
      if (!selectedBrand) return;
      setEvaluatingUrl(url);
      try {
        const res = await createEvaluation(selectedBrand.id, { page_url: url });
        if (res.cached) {
          addToast('Returning cached result from the last 60 minutes.', 'success');
        } else {
          addToast('Evaluation queued — opening detail page.', 'success');
        }
        navigate(`/admin/evaluations/${res.evaluation_id}`);
      } catch (err) {
        addToast(
          err instanceof Error ? err.message : 'Failed to queue evaluation',
          'error',
        );
      } finally {
        setEvaluatingUrl(null);
      }
    },
    [selectedBrand, addToast, navigate],
  );

  const fetchUrls = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (urlTypeFilter) params.set('url_type', urlTypeFilter);
      if (searchQuery) params.set('search', searchQuery);

      const [urlResult, typeResult] = await Promise.all([
        apiGet<UrlsResponse>(
          `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/urls?${params.toString()}`
        ),
        apiGet<UrlTypeDistribution>(
          `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/urls/types`
        ),
      ]);
      setUrls(urlResult.data);
      setTotal(urlResult.total);
      setTypeDistribution(typeResult.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load URLs');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, urlTypeFilter, searchQuery]);

  useEffect(() => { fetchUrls(); }, [fetchUrls]);

  if (!selectedBrand) {
    return (
      <div className="hub-page--table" data-density="compact">
        <h1 className="hub-page__title">Source URLs</h1>
        <EmptyState icon="bx-link" title="Select a brand" description="Choose a brand to view URL citations." />
      </div>
    );
  }

  const totalTypeCount = typeDistribution.reduce((sum, t) => sum + t.count, 0);

  // Spec 089c — only render the "GSC impressions (28d)" column when the
  // backend has included `gsc_impressions_28d` on at least one row. That
  // happens iff flag.subsystem.search_console is on. When off, every row
  // omits the field and we hide the entire column.
  const showGscColumn = urls.some((u) => u.gsc_impressions_28d !== undefined);

  return (
    <div data-testid="urls-page" className="hub-page--table" data-density="compact">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Source URLs</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">All URLs cited in AI responses for {selectedBrand.name}</p>
        </div>
      </div>

      {/* URL type distribution. Spec 249: these were eight bordered cards that
          doubled as a filter toggle — a second, unlabelled way to set the Type
          select below. The distribution is information, so it is a definition
          row; filtering is the select's job. */}
      {typeDistribution.length > 0 && (
        <dl className="hub-deflist" data-testid="url-type-distribution">
          {typeDistribution.map((t) => (
            <div className="hub-deflist__item" key={t.url_type}>
              <dt>{URL_TYPE_LABELS[t.url_type] ?? t.url_type}</dt>
              <dd>
                {t.count}
                <span className="hub-deflist__extra">
                  , {totalTypeCount > 0 ? Math.round((t.count / totalTypeCount) * 100) : 0}%
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Filters sit directly above the rows they filter. */}
      <div className="hub-toolbar">
        {/* Spec 246 (review P0-2): Kridt's Field-with-icon owns the geometry, so the
            text can never start under the glyph. */}
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="urls-search">Search</label>
        <span className="hub-input-wrap">
          <Icon name="bx-search" />
          <input
            id="urls-search"
            type="search"
            placeholder="URL contains…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="hub-input"
          />
        </span>
        </div>
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="urls-type">Type</label>
        <Select
          id="urls-type"
          options={[
            { value: '', label: 'All URL types' },
            ...Object.entries(URL_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
          ]}
          value={urlTypeFilter}
          onChange={setUrlTypeFilter}
          placeholder="All URL types"
          className="w-44"
        />
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="flex justify-center py-8"><Spinner className="text-[var(--hub-color-action-text)]" /></div>}

      {!loading && urls.length === 0 && (
        <EmptyState icon="bx-link" title="No URLs found" description="Run queries to start collecting URL citation data." />
      )}

      {!loading && urls.length > 0 && (
        <>
          <p className="text-sm text-[var(--hub-color-ink-muted)]">{total} URLs found</p>
          <div className="overflow-x-auto">
            <table className="hub-table" data-testid="urls-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>URL type</th>
                  <th>Domain type</th>
                  <th>Brand</th>
                  <th className="is-num">Mentions</th>
                  <th className="is-num">Engines</th>
                  <th>Last cited</th>
                  {showGscColumn && (
                    <th
                      className="is-num"
                      title="Sum of impressions across all Google search appearances (AI Overview, AI Mode, Featured snippet, etc.) over the last 28 days."
                      data-testid="urls-table-gsc-header"
                    >
                      GSC impressions (28d)
                    </th>
                  )}
                  {canEvaluate && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {urls.map((url) => (
                  <tr key={url.url}>
                    <td className="max-w-xs truncate">
                      <a href={url.url} target="_blank" rel="noopener noreferrer" className="text-[var(--hub-color-action-text)]">
                        {url.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                      </a>
                    </td>
                    <td><UrlTypeBadge type={url.url_type ?? 'other'} /></td>
                    <td className="text-xs">{url.domain_type ?? '--'}</td>
                    <td>
                      {url.brand_mentioned === true && <span className="hub-chip hub-chip--ok">Yes</span>}
                      {url.brand_mentioned === false && <span className="hub-chip hub-chip--fail">No</span>}
                      {url.brand_mentioned === null && <span className="text-xs text-[var(--hub-color-ink-subtle)]">--</span>}
                    </td>
                    <td className="is-num">{url.mention_count}</td>
                    <td className="is-num">{url.engine_count}</td>
                    <td className="text-xs">
                      {url.last_cited ? new Date(url.last_cited).toLocaleDateString() : '--'}
                    </td>
                    {showGscColumn && (
                      <td
                        className="is-num"
                        data-testid={`urls-table-gsc-cell-${url.url}`}
                        title={
                          url.gsc_impressions_28d && url.gsc_impressions_28d > 0
                            ? `${url.gsc_impressions_28d.toLocaleString()} impressions in the last 28 days`
                            : 'No AI-surface impressions in the last 28 days'
                        }
                      >
                        {url.gsc_impressions_28d && url.gsc_impressions_28d > 0
                          ? url.gsc_impressions_28d.toLocaleString()
                          : <span className="text-[var(--hub-color-ink-subtle)]">--</span>}
                      </td>
                    )}
                    {canEvaluate && (
                      <td className="text-right">
                        <button
                          type="button"
                          data-testid={`evaluate-url-${url.url}`}
                          onClick={() => handleEvaluate(url.url)}
                          disabled={evaluatingUrl === url.url}
                          title="Evaluate this URL against the active GEO rule set"
                          className="hub-btn hub-btn--ghost"
                        >
                          <Icon name="bx-play-circle" />
                          {evaluatingUrl === url.url ? 'Queuing…' : 'Evaluate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
